import { FC, useEffect, useCallback, useRef } from "react";

import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import { isDrawingFormCategory, Locales, PrimaryMainColor } from "@simplyk/common";
import { renderToStaticMarkup } from "react-dom/server";

import { useCurrentUserContext } from "../../../../../common/contexts/CurrentUserContext";
import { mapDataToProvideRates } from "../../../../../common/helpers/form-edition";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";

import { CheckInTooltip } from "./CheckInTooltip/CheckInTooltip";
import { useCheckInTooltipPlacement } from "./helpers/checkInTooltip";
import { syncScanAnimation } from "./helpers/scanAnimation";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useLocaleField } from "@/canvas/features/FormV2/helpers/useLocaleField";
import { ETicketPDFPreview } from "@/components/PDFPreview/ETicketPDFPreview";
import { useTranslate } from "@/common/hooks/useTranslate";
import { getPreviewEventDates } from "@/features/LiveFormEditor/LivePreview/ETicketLivePreview/helpers/getPreviewEventDates";
import { Check as FilledCheck } from "@/icons/filled";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

export interface ETicketCheckInPromotion {
  isAnimationFrozen: boolean;
  isTooltipVisible: boolean;
  onTooltipDismiss: () => void;
  onMoreAboutCheckInClick: () => void;
}

interface ETicketLivePreviewProps {
  checkInPromotion?: ETicketCheckInPromotion;
}

export const ETicketLivePreview: FC<ETicketLivePreviewProps> = ({ checkInPromotion }) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const { liveFormData, apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const { organization } = useCurrentUserContext();
  const getField = useLocaleField();

  const mappedRates = mapDataToProvideRates(
    liveFormData?.ticketing.rates,
    liveFormData?.ticketing.locale || Locales.EN
  );
  const ticketingField = getField(liveFormData?.ticketing.ticketingFields);

  const eventDates = getPreviewEventDates({
    occurrences: apiTicketing.occurrences,
    recurrences: apiTicketing.recurrences,
  });

  // Use a constant value for the query to prevent re-runs
  const BANNER_PLACEHOLDER = "banner-placeholder";

  const liveBannerPdfUrl = liveFormData?.ticketing.bannerPdfUrl || "";

  const defaultBannerPdfUrl = apiTicketing.bannerPdfUrl || apiTicketing.bannerUrl || BANNER_PLACEHOLDER;
  const getPreviewETicketInHtmlInput = {
    bannerPdfUrl: defaultBannerPdfUrl, // We do not use the liveBannerPdfUrl here because we do not want to re-run the query when the banner changes, but use DOM manipulation instead
    address: apiTicketing.address || null,
    eventTimezone: apiTicketing.eventTimezone || null,
    rateTitle: mappedRates?.[0]?.name || null,
    formTitle: ticketingField?.title || null,
    organizationName:
      (isDrawingFormCategory(apiTicketing.formCategory) ? organization?.legalName : organization?.name) ?? null,
    startUtc: eventDates?.startUtc || null,
    endUtc: eventDates?.endUtc || null,
    ticketingColor: liveFormData?.ticketing.color || PrimaryMainColor,
    formLocale: liveFormData?.ticketing.locale || Locales.EN,
    organizationCountry: organization?.country ?? null,
    formCategory: apiTicketing.formCategory,
    isSweepstakes: isSweepstakesCategory(apiTicketing.formCategory),
    seats: apiTicketing.seats ?? null,
  };

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isTooltipVisible = Boolean(checkInPromotion?.isTooltipVisible);
  const { placement: tooltipPlacement, measureAnchor } = useCheckInTooltipPlacement({
    iframeRef,
    containerRef,
    isEnabled: isTooltipVisible,
  });

  const updateBannerInIframe = useCallback(() => {
    if (!liveFormData?.ticketing || !liveFormData?.ticketing.generateQrCode) {
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      return;
    }

    const bannerContainer = iframeDoc.querySelector(".event-banner") as HTMLDivElement;
    if (!bannerContainer) {
      return;
    }

    if (!liveBannerPdfUrl) {
      bannerContainer.style.display = "none";
      return;
    }

    bannerContainer.style.display = "block";

    const bannerImg = bannerContainer.querySelector("#ticket-banner-preview") as HTMLImageElement;
    if (bannerImg) {
      bannerImg.src = liveBannerPdfUrl;
    }
  }, [liveBannerPdfUrl, liveFormData?.ticketing, iframeRef]);

  const isScanAnimationEnabled = Boolean(checkInPromotion);
  const isScanAnimationFrozen = Boolean(checkInPromotion?.isAnimationFrozen);
  const scanAnimationLabel = t("dashboard", "ticketing.setETicket.scanAnimation.guestCheckedIn");

  const updateScanAnimationInIframe = useCallback(() => {
    const iframeDocument = iframeRef.current?.contentDocument;
    if (!isScanAnimationEnabled || !iframeDocument) {
      return;
    }
    syncScanAnimation(iframeDocument, {
      isFrozen: isScanAnimationFrozen,
      label: scanAnimationLabel,
      checkIconMarkup: renderToStaticMarkup(<FilledCheck />),
      theme: {
        accentColor: theme.palette.text.positive.quiet,
        overlayColor: alpha(theme.palette.surface.neutral.white, 0.95),
        labelColor: theme.palette.text.neutral.intense,
        labelFontWeight: theme.typography.fontWeightSemiBold,
      },
    });
  }, [isScanAnimationEnabled, isScanAnimationFrozen, scanAnimationLabel, theme]);

  // Add a load event listener to the iframe
  const handleIframeLoad = useCallback(() => {
    updateBannerInIframe();
    updateScanAnimationInIframe();
    measureAnchor();
  }, [updateBannerInIframe, updateScanAnimationInIframe, measureAnchor]);

  // Update banner when URL changes
  useEffect(() => {
    updateBannerInIframe();
  }, [liveBannerPdfUrl, updateBannerInIframe]);

  useEffect(() => {
    updateScanAnimationInIframe();
  }, [updateScanAnimationInIframe]);

  // If the ticketing object says no QR code, return null
  if (liveFormData?.ticketing && !liveFormData?.ticketing.generateQrCode) {
    return null;
  }

  return (
    <Box ref={containerRef} sx={{ position: "relative", height: "100%", width: "100%" }}>
      <ETicketPDFPreview
        handleIframeLoad={handleIframeLoad}
        getPreviewETicketInHtmlInput={getPreviewETicketInHtmlInput}
        iframeRef={iframeRef}
      />
      {checkInPromotion && tooltipPlacement && (
        <CheckInTooltip
          placement={tooltipPlacement}
          onClose={checkInPromotion.onTooltipDismiss}
          onMoreAboutCheckInClick={checkInPromotion.onMoreAboutCheckInClick}
        />
      )}
    </Box>
  );
};
